import React from "react";
import { Clock, Loader2, Power, PowerOff, Settings2 } from "lucide-react";
import { cn } from "@/lib/utils";
import paymentIntegrationService from "@/services/paymentIntegrationService";

/** Status exibido no card: ativa > configurada > não configurada; sem adaptador = "Em breve" */
export const getIntegrationStatus = (integration) => {
  if (!integration?.implemented) return "soon";
  if (integration.enabled) return "active";
  if (integration.configured) return "configured";
  return "pending";
};

const STATUS = {
  active: {
    label: "Ativa",
    className: "border-brand-pistachio/50 bg-secondary text-secondary-foreground",
  },
  configured: {
    label: "Configurada · inativa",
    className: "border-border bg-surface-container text-muted-foreground",
  },
  pending: {
    label: "Não configurada",
    className: "border-terra/30 bg-terra-soft text-terra",
  },
  soon: {
    label: "Em breve",
    className: "border-border bg-muted text-muted-foreground",
  },
};

/** Um provedor: nome, status, dados da conta e ações (configurar, ativar/desativar) */
function PaymentIntegrationCard({ integration, busy, onConfigure, onActivate, onDeactivate }) {
  const status = getIntegrationStatus(integration);
  const name = integration.providerName || integration.provider;
  const implemented = Boolean(integration.implemented);
  const updatedAt = paymentIntegrationService.formatUpdatedAt(integration.updatedAt);

  return (
    <article
      className={cn(
        "relative flex flex-col rounded-2xl border bg-card p-4 shadow-soft transition-all",
        status === "active" ? "border-2 border-primary/60" : "border-border",
        status === "soon" && "bg-surface-low"
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl font-display text-base font-bold",
            implemented ? "bg-surface-container text-primary" : "bg-muted text-muted-foreground"
          )}
          aria-hidden="true"
        >
          {name.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <h3
            className={cn("truncate text-sm font-bold leading-tight", implemented ? "text-foreground" : "text-muted-foreground")}
            title={name}
          >
            {name}
          </h3>
          <span
            className={cn(
              "mt-1 inline-flex items-center rounded border px-1.5 text-[10px] font-medium",
              STATUS[status].className
            )}
          >
            {STATUS[status].label}
          </span>
        </div>
      </div>

      <div className="mt-3 flex-1 space-y-1 text-xs">
        {!implemented ? (
          <p className="text-muted-foreground">Integração ainda não disponível nesta versão do sistema.</p>
        ) : integration.configured ? (
          <>
            {integration.handle && (
              <p className="truncate text-muted-foreground">
                InfiniteTag: <span className="font-mono font-semibold text-foreground">${integration.handle}</span>
              </p>
            )}
            {updatedAt && (
              <p className="flex items-center gap-1 truncate text-[11px] text-muted-foreground">
                <Clock className="h-3 w-3 flex-shrink-0" />
                Atualizada em {updatedAt}
                {integration.updatedBy ? ` por ${integration.updatedBy}` : ""}
              </p>
            )}
          </>
        ) : (
          <p className="text-muted-foreground">Configure os dados da conta para receber pagamentos online.</p>
        )}
      </div>

      {implemented && (
        <div className="mt-3 flex items-center justify-end gap-2 border-t border-border/60 pt-3">
          {busy && <Loader2 className="mr-auto h-4 w-4 animate-spin text-muted-foreground" />}
          <button
            type="button"
            onClick={() => onConfigure(integration)}
            disabled={busy}
            className="flex h-8 items-center gap-1 rounded-full border border-border bg-surface-container px-3 text-xs font-medium text-foreground transition-colors hover:bg-surface-high disabled:opacity-60"
          >
            <Settings2 className="h-3.5 w-3.5" />
            Configurar
          </button>
          {integration.enabled ? (
            <button
              type="button"
              onClick={() => onDeactivate(integration)}
              disabled={busy}
              className="flex h-8 items-center gap-1 rounded-full border border-destructive/25 bg-destructive/5 px-3 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-60"
            >
              <PowerOff className="h-3.5 w-3.5" />
              Desativar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onActivate(integration)}
              disabled={busy || !integration.configured}
              title={integration.configured ? undefined : "Configure a integração antes de ativar"}
              className="flex h-8 items-center gap-1 rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground transition-colors hover:bg-brand-dark disabled:opacity-50"
            >
              <Power className="h-3.5 w-3.5" />
              Ativar
            </button>
          )}
        </div>
      )}
    </article>
  );
}

export default React.memo(PaymentIntegrationCard);
